import React, { useState, useEffect, useRef } from "react";
import { Modal, Form } from "react-bootstrap";
import CommonButton from "../../commonButton";
import { Cancel01Icon, Add01Icon } from "../../../assets/icons";
import withModal from "../../withModal";
import { useToast } from "../../../context/TostContext";
import InputField from "../../form/InputField";
import { FormLabel } from "../../form/utils";

const CompanyProductModalInner = ({ onClose, onSubmit, product, isLoading }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const fileInputRef = useRef(null);
  const toast=useToast();
  useEffect(() => {
    if (product) {
      setFormData({
        title: product.title || "",
        description: product.description || "",
      });
      setExistingMedia(product.media || []);
      setFiles([]);
      setFilePreviews([]);
      setRemovedImages([]);
    } else {
      setFormData({
        title: "",
        description: "",
      });
      setExistingMedia([]);
      setFiles([]);
      setFilePreviews([]);
      setRemovedImages([]);
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const currentTotal = files.length + existingMedia.length - removedImages.length;
    
    if (currentTotal + selectedFiles.length > 3) {
      toast.error("You can only upload up to 3 files in total");
      return;
    }

    const newFiles = [...files];
    const newPreviews = [...filePreviews];

    selectedFiles.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return;
      }
      newFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    });

    setFiles(newFiles);
    setFilePreviews(newPreviews);
    
    if (fileInputRef.current) {
        fileInputRef.current.value = "";
    }
  };

  const removeNewFile = (index) => {
    const newFiles = [...files];
    const newPreviews = [...filePreviews];
    
    URL.revokeObjectURL(newPreviews[index]);
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    
    setFiles(newFiles);
    setFilePreviews(newPreviews);
  };

  const removeExistingFile = (id) => {
    setRemovedImages((prev) => [...prev, id]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (!formData.title) {
      toast.error("Please enter a product title");
      return;
    }

    const activeExistingMedia = existingMedia.filter(m => !removedImages.includes(m.id));
    if (files.length === 0 && activeExistingMedia.length === 0) {
      toast.error("Please upload at least one product image");
      return;
    }

    const submitData = new FormData();
    submitData.append("title", formData.title);
    submitData.append("description", formData.description);

    files.forEach(file => {
      submitData.append("media", file); // Must be "media" to match multer
    });

    if (product) {
        const keptMediaIds = existingMedia.map(m => m.id).filter(id => !removedImages.includes(id));
        if (keptMediaIds.length > 0) {
            keptMediaIds.forEach(id => {
                submitData.append("existing_media_ids", id);
            });
        }
    }

    onSubmit(submitData);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Modal.Body className="grid row-gap-3">
        <InputField
          className="g-col-12"
          label={{ name: "Product Title" }}
          required
          input={{
            type: "text",
            name: "title",
            value: formData.title,
            onChange: handleChange,
            placeholder: "Ex: Industrial Concrete Mixer",
            required: true,
          }}
        />
        
        <InputField
          className="g-col-12"
          label={{ name: "Description" }}
          input={{
            as: "textarea",
            rows: 3,
            name: "description",
            value: formData.description,
            onChange: handleChange,
            placeholder: "Describe the product features...",
          }}
        />
        
        <Form.Group className="g-col-12">
          <FormLabel labelName="Product Photos (Max 3 images)"/>
          <div className="d-flex flex-wrap gap-3 mb-2">
            {/* Existing Media */}
            {existingMedia
              .filter(m => !removedImages.includes(m.id))
              .map((m) => (
                <div 
                  key={m.id} 
                  className="position-relative border rounded"
                  style={{ width: "100px", height: "100px" }}
                >
                  <img 
                    src={m.file?.url} 
                    alt="Product" 
                    className="w-100 h-100 rounded object-fit-cover" 
                  />
                  <button
                    type="button"
                    className="position-absolute top-0 end-0 badge text-white rounded-circle bg-secondary border-0 p-1 d-flex justify-content-center align-items-center"
                    style={{
                      width: "20px",
                      height: "20px",
                      cursor: "pointer",
                      transform: "translate(50%, -50%)",
                    }}
                    onClick={() => removeExistingFile(m.id)}
                  >
                    <Cancel01Icon size={12} />
                  </button>
                </div>
              ))}

            {/* New Media Previews */}
            {filePreviews.map((url, index) => (
              <div key={index} className="position-relative border rounded" style={{ width: "100px", height: "100px" }}>
                <img src={url} alt="Preview" className="w-100 h-100 rounded object-fit-cover" />
                <button
                  type="button"
                  className="position-absolute top-0 end-0 text-white badge rounded-circle bg-secondary border-0 p-1 d-flex justify-content-center align-items-center"
                  style={{
                    width: "20px",
                    height: "20px",
                    cursor: "pointer",
                    transform: "translate(50%, -50%)",
                  }}
                  onClick={() => removeNewFile(index)}
                >
                  <Cancel01Icon size={12}  />
                </button>
              </div>
            ))}

            {/* Upload Button */}
            {(files.length + existingMedia.length - removedImages.length) < 3 && (
              <button 
                className="border rounded-2 d-flex align-items-center justify-content-center cursor-pointer mb-2"
                style={{ width: "100px", height: "100px", backgroundColor: "#f8f9fa", borderStyle: 'dashed' }}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="text-center">
                  <Add01Icon size={24} />
                  <div className="small text-muted">Add</div>
                </div>
              </button>
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            multiple
            className="d-none"
          />
          <Form.Text className="text-muted d-block mt-2">Total 3 images allowed. Max 5MB per file.</Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <CommonButton
          type="button"
          variant="outline"
          title="Cancel"
          onClick={onClose}
          disabled={isLoading}
        />
        <CommonButton
          type="submit"
          variant="primary"
          title={product ? "Update" : "Save"}
          isLoading={isLoading}
        />
      </Modal.Footer>
    </Form>
  );
};
const CompanyProductModal = withModal(CompanyProductModalInner);
export default CompanyProductModal;

